import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@base/components/ui/button';
import { Slider } from '@base/components/ui/slider';
import {
  VideoEventData,
  VideoEventName
} from '@components/assignments/task/blocks/slides/hooks/useVideoEventLogger';
import { formatTime } from '@datetime';
import { useAccountsService } from '@hooks';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MaximizeIcon,
  MinimizeIcon,
  PauseIcon,
  PlayIcon,
  SkipBackwardIcon,
  SkipForwardIcon,
  VolumeIcon
} from '@icons';
import { MediaitemModel } from '@models';
import { setupForDark } from '@theme';
import { useClickAway, useNetworkState } from '@uidotdev/usehooks';
import classNames from 'classnames';
import { MediaitemService } from 'lib/services/mediaitems';
import MediaitemAccessError, { MediaitemErrorCode } from './MediaitemAccessError';
import VimeoPlayer from './VimeoPlayer';

const PLAYBACK_SPEEDS = [0.5, 0.75, 1, 1.25, 1.5, 2];
const SKIP_STEP = 5;

const MediaitemVideoPlayer = ({
  mediaitem,
  width,
  height,
  currentTimeSeconds,
  onVideoEvent
}: {
  mediaitem: MediaitemModel;
  width: number;
  height: number;
  currentTimeSeconds?: number;
  onVideoEvent: (event: VideoEventName, data: VideoEventData) => void;
}) => {
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [accessError, setAccessError] = useState({
    hasAccessError: false,
    errorCode: undefined
  });
  const videoRef = useRef<HTMLVideoElement>(null);
  const volumeControlRef = useRef<HTMLDivElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const network = useNetworkState();
  const ref = useClickAway<HTMLDivElement>((event) => {
    if (isFocused) {
      setIsFocused(false);
    }
  });

  const { videoPlaybackSpeed, setVideoPlaybackSpeed } = useAccountsService();

  const [controlsVisible, setControlsVisible] = useState(false);
  const hideControlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const mediaitemService = useMemo(() => new MediaitemService(mediaitem), [mediaitem]);

  const videoSrc = mediaitemService.getVideoSrc();
  const isVimeo = mediaitemService.isVimeoSource();

  // Load saved playback speed on component mount
  useEffect(() => {
    const loadSavedSpeed = async () => {
      try {
        if (videoRef.current) {
          videoRef.current.playbackRate = videoPlaybackSpeed;
        }
      } catch (error) {
        console.error('Error loading saved playback speed:', error);
      }
    };

    if (!isVimeo) {
      loadSavedSpeed();
    }
  }, [videoPlaybackSpeed, isVimeo]);

  const togglePlay = async () => {
    if (videoRef.current) {
      try {
        if (videoRef.current.paused) {
          const playPromise = videoRef.current.play();
          if (playPromise !== undefined) {
            await playPromise;
          }
        } else {
          videoRef.current.pause();
        }
      } catch (error) {
        // Handle any play/pause errors gracefully
        console.error('Playback error:', error);
        setAccessError({
          hasAccessError: true,
          errorCode: MediaitemErrorCode.MEDIA_ERR_SRC_NOT_SUPPORTED
        });
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  useEffect(() => {
    setAccessError({
      hasAccessError: false,
      errorCode: undefined
    });
  }, [mediaitem]);

  /**
   * DURATION
   */
  // attempt to load duration from mediaitemService
  // this will not be available if the ORIGINAL file is not ready
  // for IMPROVERS the video is already processed therefore the duration is available
  // and it will be loaded no matter what are the browser limitations
  useEffect(() => {
    setDuration(mediaitemService.getDuration());
  }, [mediaitemService]);

  // always load duration from video element, overrides mediaitemService
  useEffect(() => {
    if (isVimeo) return;

    const videoElement = videoRef.current;
    if (videoElement) {
      const handleLoadedMetadata = () => {
        const videoDuration = videoElement.duration;
        if (!isNaN(videoDuration)) {
          setDuration(videoDuration);
        }
      };

      const handlePlay = () => {
        setDuration(videoElement.duration);
      };

      videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
      videoElement.addEventListener('play', handlePlay);

      return () => {
        videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
        videoElement.removeEventListener('play', handlePlay);
      };
    }
  }, [videoRef, isVimeo]);

  const handleProgressChange = (value: number[]) => {
    if (videoRef.current && typeof value[0] === 'number' && isFinite(value[0])) {
      videoRef.current.currentTime = value[0];
      setCurrentTime(value[0]);
    }
  };

  const handleVolumeChange = (value: number[]) => {
    if (videoRef.current) {
      const newVolume = value[0];
      videoRef.current.volume = newVolume;
      setVolume(newVolume);
      setMuted(newVolume === 0);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      const newMutedState = !muted;
      videoRef.current.muted = newMutedState;
      setMuted(newMutedState);
      if (newMutedState) {
        videoRef.current.volume = 0;
        setVolume(0);
      } else {
        videoRef.current.volume = volume > 0 ? volume : 1;
        setVolume(volume > 0 ? volume : 1);
      }
    }
  };

  const skipBackward = () => {
    if (videoRef.current)
      videoRef.current.currentTime = Math.max(0, videoRef.current.currentTime - SKIP_STEP);
  };

  const skipForward = () => {
    if (videoRef.current)
      videoRef.current.currentTime = Math.min(duration, videoRef.current.currentTime + SKIP_STEP);
  };

  // Save playback speed when it changes
  const handleSpeedChange = async (value: string) => {
    try {
      const speed = Number.parseFloat(value);
      if (videoRef.current && !isNaN(speed)) {
        // Ensure video is focused when changing speed
        videoRef.current.focus();
        videoRef.current.playbackRate = speed;
        setVideoPlaybackSpeed(speed);
      }
    } catch (error) {
      console.error('Speed change error:', error);
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (!isFocused) return;
    setControlsVisible(true);
    resetHideControlsTimeout();

    switch (event.code) {
      case 'ArrowLeft':
        skipBackward();
        break;
      case 'ArrowRight':
        skipForward();
        break;
      case 'ArrowUp': {
        if (videoRef.current) {
          const newVolume = Math.min((videoRef.current.volume ?? 1) + 0.1, 1);
          videoRef.current.volume = newVolume;
          setVolume(newVolume);
          setMuted(newVolume === 0);
        }
        break;
      }
      case 'ArrowDown': {
        if (videoRef.current) {
          const newVolume = Math.max((videoRef.current.volume ?? 1) - 0.1, 0);
          videoRef.current.volume = newVolume;
          setVolume(newVolume);
          setMuted(newVolume === 0);
        }
        break;
      }
      case 'm':
        toggleMute();
        break;
      case 'f':
        onToggleFullscreen();
        break;
      case 'Space': // Spacebar
        event.preventDefault();
        togglePlay();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (isFocused) {
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.removeEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isFocused]);

  useEffect(() => {
    if (isVimeo) return;

    const videoElement = videoRef.current;
    if (videoElement) {
      const handlePlay = () => setPlaying(true);
      const handlePause = () => setPlaying(false);

      videoElement.addEventListener('play', handlePlay);
      videoElement.addEventListener('pause', handlePause);

      return () => {
        videoElement.removeEventListener('play', handlePlay);
        videoElement.removeEventListener('pause', handlePause);
      };
    }
  }, [videoRef, isVimeo]);

  const handleVideoClick = () => {
    if (!isVimeo && videoRef.current) {
      videoRef.current.focus();
      setIsFocused(true);
      togglePlay();
    }
    setControlsVisible(true);
    resetHideControlsTimeout();
  };

  const handlePlayPauseClick = () => {
    togglePlay();
  };

  const handleMouseMove = () => {
    setControlsVisible(true);
    resetHideControlsTimeout();
  };

  const resetHideControlsTimeout = () => {
    if (hideControlsTimeoutRef.current) {
      clearTimeout(hideControlsTimeoutRef.current);
    }
    hideControlsTimeoutRef.current = setTimeout(() => {
      setControlsVisible(false);
    }, 5000); // 5 seconds
  };

  useEffect(() => {
    if (isVimeo) return;

    const videoElement = videoRef.current;
    if (videoElement) {
      videoElement.addEventListener('mousemove', handleMouseMove);
    }

    return () => {
      if (videoElement) {
        videoElement.removeEventListener('mousemove', handleMouseMove);
      }
      if (hideControlsTimeoutRef.current) {
        clearTimeout(hideControlsTimeoutRef.current);
      }
    };
  }, [isVimeo]);

  const handleControlsClick = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  const onToggleFullscreen = () => {
    const videoContainer: any = videoRef.current?.parentElement;
    if (videoContainer) {
      if (document.fullscreenElement) {
        // If already in fullscreen, exit fullscreen
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if ((document as any).mozCancelFullScreen) {
          // Firefox
          (document as any).mozCancelFullScreen();
        } else if ((document as any).webkitExitFullscreen) {
          // Chrome, Safari, and Opera
          (document as any).webkitExitFullscreen();
        } else if ((document as any).msExitFullscreen) {
          // IE/Edge
          (document as any).msExitFullscreen();
        }
        setIsFullscreen(false);
      } else {
        // If not in fullscreen, enter fullscreen
        if (videoContainer.requestFullscreen) {
          videoContainer.requestFullscreen();
        } else if (videoContainer.mozRequestFullScreen) {
          // Firefox
          videoContainer.mozRequestFullScreen();
        } else if (videoContainer.webkitRequestFullscreen) {
          // Chrome, Safari, and Opera
          videoContainer.webkitRequestFullscreen();
        } else if (videoContainer.msRequestFullscreen) {
          // IE/Edge
          videoContainer.msRequestFullscreen();
        }
        setIsFullscreen(true);
      }
    }
  };

  const buttonsEl = useMemo(
    () => (
      <div className='flex items-center gap-2'>
        <Button
          variant='ghost'
          size='icon'
          className='h-8 w-8 hover:scale-110'
          onClick={skipBackward}
        >
          <SkipBackwardIcon className='h-4 w-4' />
        </Button>

        <Button
          variant='ghost'
          size='icon'
          className='h-8 w-8 hover:scale-110'
          onClick={handlePlayPauseClick}
        >
          {playing ? (
            <PauseIcon className='h-4 w-4 fill-current' />
          ) : (
            <PlayIcon className='h-4 w-4 fill-current' />
          )}
        </Button>

        <Button
          variant='ghost'
          size='icon'
          className='h-8 w-8 hover:scale-110'
          onClick={skipForward}
        >
          <SkipForwardIcon className='h-4 w-4' />
        </Button>
      </div>
    ),
    [playing, skipBackward, skipForward, handlePlayPauseClick]
  );

  const leftControlsEl = useMemo(
    () => (
      <div ref={volumeControlRef} className='relative flex items-center gap-xs'>
        <Button
          variant='ghost'
          size='icon'
          className='h-8 w-8 hover:scale-110'
          onClick={toggleMute}
        >
          {muted || volume === 0 ? <VolumeIcon size={16} /> : <VolumeIcon size={16} />}
        </Button>
        <Slider
          className={classNames('h-[15px] w-[60px]', setupForDark('bg-transparent'))}
          value={[muted ? 0 : volume]}
          min={0}
          max={1}
          step={0.01}
          onValueChange={handleVolumeChange}
        />
      </div>
    ),
    [muted, volume, toggleMute, handleVolumeChange]
  );

  const rightControlsEl = useMemo(() => {
    const currentSpeedIndex = PLAYBACK_SPEEDS.indexOf(videoPlaybackSpeed);
    const canDecreaseSpeed = currentSpeedIndex > 0;
    const canIncreaseSpeed = currentSpeedIndex < PLAYBACK_SPEEDS.length - 1;

    const decreaseSpeed = () => {
      if (canDecreaseSpeed) {
        handleSpeedChange(PLAYBACK_SPEEDS[currentSpeedIndex - 1].toString());
      }
    };

    const increaseSpeed = () => {
      if (canIncreaseSpeed) {
        handleSpeedChange(PLAYBACK_SPEEDS[currentSpeedIndex + 1].toString());
      }
    };

    return (
      <div className='relative flex items-center gap-lg'>
        <div className='flex items-center'>
          <Button
            variant='ghost'
            size='icon'
            className='h-8 w-8'
            onClick={decreaseSpeed}
            disabled={!canDecreaseSpeed}
          >
            <ChevronLeftIcon size={16} />
          </Button>
          <span className='min-w-[35px] text-center P4R'>{videoPlaybackSpeed}x</span>
          <Button
            variant='ghost'
            size='icon'
            className='h-8 w-8'
            onClick={increaseSpeed}
            disabled={!canIncreaseSpeed}
          >
            <ChevronRightIcon size={16} />
          </Button>
        </div>

        <div className='hidden dk:flex'>
          {isFullscreen ? (
            <MinimizeIcon
              size={24}
              className='cursor-pointer hover:scale-110'
              onClick={onToggleFullscreen}
            />
          ) : (
            <MaximizeIcon
              size={24}
              className='cursor-pointer hover:scale-110'
              onClick={onToggleFullscreen}
            />
          )}
        </div>
      </div>
    );
  }, [videoPlaybackSpeed, handleSpeedChange, isFullscreen, onToggleFullscreen]);

  useEffect(() => {
    // Handle network state
    if (!network.online) {
      setAccessError({
        hasAccessError: true,
        errorCode: MediaitemErrorCode.MEDIA_ERR_NETWORK
      });
    } else if (
      accessError.hasAccessError &&
      accessError.errorCode === MediaitemErrorCode.MEDIA_ERR_NETWORK
    ) {
      // Clear the network error when coming back online
      setAccessError({
        hasAccessError: false,
        errorCode: undefined
      });
    }

    // Handle video element errors
    if (videoRef.current && !isVimeo) {
      const handleError = (e: Event) => {
        const video = e.target as HTMLVideoElement;
        // Only set the error if it's not already a network error
        if (
          video.error &&
          (!accessError.hasAccessError ||
            accessError.errorCode !== MediaitemErrorCode.MEDIA_ERR_NETWORK)
        ) {
          setAccessError({
            hasAccessError: true,
            errorCode: video.error.code
          });
        }
      };

      videoRef.current.addEventListener('error', handleError);
      return () => {
        videoRef.current?.removeEventListener('error', handleError);
      };
    }
  }, [network.online, videoRef, accessError, isVimeo]);

  useEffect(() => {
    if (videoRef.current && !isVimeo) {
      videoRef.current.focus();
      setIsFocused(true);
    }
  }, [isVimeo]); // Only on mount

  // HTML5 Video: Handle currentTimeSeconds
  useEffect(() => {
    if (
      !isVimeo &&
      videoRef.current &&
      currentTimeSeconds !== undefined &&
      !isNaN(currentTimeSeconds)
    ) {
      const videoElement = videoRef.current;

      const handleLoadedMetadata = () => {
        videoElement.currentTime = currentTimeSeconds!;
      };

      if (videoElement.readyState >= 1) {
        handleLoadedMetadata();
      } else {
        videoElement.addEventListener('loadedmetadata', handleLoadedMetadata);
        return () => videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      }
    }
  }, [currentTimeSeconds, isVimeo]);

  if (accessError.hasAccessError) {
    return (
      <div ref={ref} className={classNames('w-full', 'h-full')}>
        <MediaitemAccessError type='video' className='h-full' errorCode={accessError.errorCode} />
      </div>
    );
  }

  // Render the Vimeo player if it's a Vimeo source
  if (isVimeo) {
    return (
      <div ref={ref} className={classNames('w-full')}>
        <VimeoPlayer
          mediaitem={mediaitem}
          width={width}
          height={height}
          currentTimeSeconds={currentTimeSeconds}
          onVideoEvent={onVideoEvent}
        />
      </div>
    );
  }

  // Otherwise render the standard video player
  return (
    <div ref={ref} className={classNames('w-full', 'h-full')} onClick={handleVideoClick}>
      <div className='relative group w-full h-full overflow-hidden'>
        <video
          ref={videoRef}
          className='w-full h-full cursor-pointer object-contain'
          onTimeUpdate={handleTimeUpdate}
          tabIndex={0}
        >
          {videoSrc && <source src={videoSrc} type='video/mp4' />}
        </video>

        <div
          className={classNames(
            'absolute bottom-0 left-0 right-0 transition-transform duration-200',
            controlsVisible ? 'translate-y-0' : 'translate-y-full'
          )}
          onClick={handleControlsClick}
        >
          <div
            className={classNames(
              'bg-gradient-to-t to-transparent p-sm',
              setupForDark('from-bg-primary/80')
            )}
          >
            <div className='w-full mb-xs flex flex-row gap-xs'>
              <Slider
                value={[currentTime]}
                min={0}
                max={duration}
                step={0.1}
                onValueChange={handleProgressChange}
              />
              <span
                className={classNames('flex flex-row gap-xxs P6R', setupForDark('text-primary'))}
              >
                <span>{formatTime(currentTime)}</span>
                <span>/</span>
                <span>{formatTime(duration)}</span>
              </span>
            </div>

            <div
              className={classNames(
                'flex items-center justify-between',
                setupForDark('text-primary')
              )}
            >
              <div className='flex-1 flex justify-start'>{leftControlsEl}</div>
              <div className='flex-1 flex justify-center'>{buttonsEl}</div>
              <div className='flex-1 flex justify-end'>{rightControlsEl}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MediaitemVideoPlayer;
